import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { GeoTechnicalAnalysisReportDataService } from '../../../../services/geo-technical-analysis-report-data.service';
import {
  GeotechnicalAnalysisService,
  GEOTECHNICAL_AI_DASHBOARD_STATUS,
  IAiDashboardVersion,
} from '../../../../services/geotechnical-analysis.service';
import { LottieAnimationComponent } from 'src/app/@shared/components/lottie-animation/lottie-animation.component';
import { GEOTECHNICAL_ANALYSIS_SVG, SVG } from 'src/app/common/svgs';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';
import { ScrollResetDirective } from 'src/app/@shared/directives/scroll-reset.directive';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';

/**
 * Standalone tab component that hosts the AI-generated interactive HTML dashboard.
 *
 * States (mirrors geo-technical-summary pattern):
 * - initialLoading         : API call in-flight on init — shows a spinner.
 * - IN_PROGRESS            : generation running — shows lottie animation.
 * - FAILED                 : generation failed — shows error + retry button.
 * - NOT_STARTED / null     : never generated — shows a "Generate" CTA button.
 * - COMPLETED + src ready  : sandboxed iframe renders the self-contained HTML.
 *   - openHistory = true   : history panel replaces the iframe.
 */
@Component({
  selector: 'app-geo-technical-ai-dashboard-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    IconComponent,
    MenuComponent,
    UserNameDisplayComponent,
    LottieAnimationComponent,
    DialogComponent,
    SanitizeHtmlPipe,
    ScrollResetDirective,
    ProgressSpinnerModule,
    CreditsOverviewComponent,
  ],
  templateUrl: './geo-technical-ai-dashboard-tab.component.html',
  styleUrl: './geo-technical-ai-dashboard-tab.component.scss',
})
export class GeoTechnicalAiDashboardTabComponent implements OnInit, OnDestroy {

  /** True while the initial GET /:id/ai-dashboard call is in flight.
   *  Starts as true so the spinner renders immediately on mount before the
   *  first API response arrives, preventing a flash of the empty "generate" CTA. */
  initialLoading = true;

  /** True while the signed-URL HTML download from S3 is in progress.
   *  Prevents a flash of the empty CTA between the API response (COMPLETED)
   *  and the moment aiDashboardSrc is ready. */
  srcLoading = false;

  /** Backend status of the AI dashboard for this analysis. */
  aiDashboardStatus = signal<GEOTECHNICAL_AI_DASHBOARD_STATUS | null>(null);

  /** The generated HTML string; null until the agent delivers it. */
  aiDashboardHtml: string | null = null;

  /**
   * Blob URL for the sandboxed iframe.
   * Recreated each time new HTML arrives so CDN scripts run in their own origin.
   */
  aiDashboardSrc: SafeResourceUrl | null = null;

  /** Download in progress flag. */
  downloadLoading = signal<boolean>(false);

  /** Lottie animation data for the generation in-progress state. */
  generationAnimationData: any;
  /** Animation data for the loading and "In Queue" states (loading-spinner Lottie) */
  queueAnimationData: any;

  /** Expose SVG constants and enum to the template. */
  readonly GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG;
  readonly SVG = SVG;
  readonly GEOTECHNICAL_AI_DASHBOARD_STATUS = GEOTECHNICAL_AI_DASHBOARD_STATUS;

  /** Controls visibility of the re-generate instructions dialog. */
  regenDialogVisible = false;

  /** User-typed instructions collected from the re-generate dialog. */
  regenInstructions = '';

  /**
   * Last per-request instructions used for this analysis (from the backend).
   * Pre-fills the regen dialog when the user opens it.
   */
  lastUserInstructions: string | null = null;

  /**
   * Workspace-level AI Dashboard instructions (from the configuration drawer).
   * Used as a fallback pre-fill when no per-analysis instructions exist.
   */
  workspaceInstructions: string | null = null;

  /**
   * True when the dialog textarea is pre-filled from workspace instructions
   * (no analysis-level instructions found). Drives the "workspace" badge.
   */
  instructionsFromWorkspace = false;

  // ── History state ─────────────────────────────────────────────────────────

  /** True when the history panel is shown instead of the iframe. */
  openHistory = false;

  /** Versions returned by the list endpoint, newest first. */
  historyList: IAiDashboardVersion[] = [];

  /** ID of the version currently shown in the iframe; updated on restore. */
  currentVersionId: string | null = null;

  /** Display name of the version currently shown in the iframe. */
  currentVersionName: string | null = null;

  /** True while the version list API call is in flight. */
  loadingHistory = false;

  /** Controls visibility of the rename dialog. */
  renameDialogVisible = false;

  /** Input bound to the rename dialog text field. */
  renameInput = '';

  /** The version currently being renamed; set when the rename dialog opens. */
  private _renameTarget: IAiDashboardVersion | null = null;

  // ── View version dialog ───────────────────────────────────────────────────

  viewDialogVisible = false;
  viewVersionTitle = '';
  viewVersionLoading = false;
  viewVersionSrc: SafeResourceUrl | null = null;

  // ── Internal ──────────────────────────────────────────────────────────────

  private _blobUrl: string | null = null;
  private _viewBlobUrl: string | null = null;
  private _socketSub: Subscription | null = null;

  constructor(
    protected geoReportDataService: GeoTechnicalAnalysisReportDataService,
    private geotechnicalAnalysisService: GeotechnicalAnalysisService,
    private socketService: SocketService,
    private toast: ToastService,
    private sanitizer: DomSanitizer,
    private creditsState: UserCreditsStateService,
  ) {
  }

  /** Reads the current analysis ID from the shared data service. */
  get analysisId(): string {
    return this.geoReportDataService.analysisReportId();
  }

  /** Credits-exhausted if the backend told us directly, OR the org's agent balance is already at zero
   *  and we have no rendered dashboard yet — covers the case where this analysis never got enqueued
   *  because an upstream agent ran out first, so no status or socket event is ever coming. */
  get creditsExhausted(): boolean {
    return this.creditsState.isExhaustedFor(this.aiDashboardStatus(), !!this.aiDashboardSrc);
  }

  ngOnInit(): void {
    this.loadGenerationAnimation();
    this.loadQueueAnimation();

    // Restore any previously generated / in-progress dashboard from the backend.
    this._loadPersistedDashboard();

    // Subscribe to socket events so the tab updates automatically when the dashboard is ready.
    // Python persists HTML to S3 and creates an Attachment; the consumer sends `attachmentId`
    // as a signal. We call getAiDashboard() for the signed URL — no raw HTML in the socket.
    this._socketSub = this.socketService
      .on<{ geotechnicalAnalysisId: string; success: boolean; status?: 'CREDITS_EXHAUSTED'; attachmentId?: string; error?: string }>(
        SOCKET_EVENTS.GEOTECHNICAL_AI_DASHBOARD_STATUS_CHANGED
      )
      .subscribe(result => {
        const payload = result?.[0];
        if (!payload || payload.geotechnicalAnalysisId !== this.analysisId) return;

        if (payload.status === 'CREDITS_EXHAUSTED') {
          this.aiDashboardStatus.set(GEOTECHNICAL_AI_DASHBOARD_STATUS.CREDITS_EXHAUSTED);
        } else if (payload.success) {
          // Fetch signed URL from Node → download HTML from S3 → render iframe
          this._loadPersistedDashboard();
        } else {
          this.aiDashboardStatus.set(GEOTECHNICAL_AI_DASHBOARD_STATUS.FAILED);
          this.toast.error(payload.error ?? 'AI dashboard generation failed');
        }
      });
  }

  ngOnDestroy(): void {
    this._socketSub?.unsubscribe();
    this._revokeBlobUrl();
    this._revokeViewBlobUrl();
  }

  // ── Public actions ────────────────────────────────────────────────────────

  /** Triggered by the "Generate AI Dashboard" button in the empty / failed state. */
  /** Triggers AI dashboard generation, forwarding optional user instructions to the agent. */
  onGenerate(userInstructions?: string): void {
    if (!this.analysisId) return;
    this.aiDashboardStatus.set(GEOTECHNICAL_AI_DASHBOARD_STATUS.IN_PROGRESS);
    this.geotechnicalAnalysisService.generateAiDashboard(this.analysisId, userInstructions).subscribe({
      next: () => {
        this.toast.info('AI Dashboard generation started. This may take a minute…');
      },
      error: () => {
        this.aiDashboardStatus.set(GEOTECHNICAL_AI_DASHBOARD_STATUS.FAILED);
        this.toast.error('Failed to start AI dashboard generation');
      },
    });
  }

  /** Opens the instructions dialog before re-running generation.
   *  Pre-fills with the last used instructions for this analysis;
   *  falls back to workspace-level instructions if none exist. */
  onRegenerate(): void {
    if (this.lastUserInstructions) {
      this.regenInstructions = this.lastUserInstructions;
      this.instructionsFromWorkspace = false;
    } else if (this.workspaceInstructions) {
      this.regenInstructions = this.workspaceInstructions;
      this.instructionsFromWorkspace = true;
    } else {
      this.regenInstructions = '';
      this.instructionsFromWorkspace = false;
    }
    this.regenDialogVisible = true;
  }

  /** Called when the user submits the re-generate dialog. */
  onSubmitRegenDialog(): void {
    this.regenDialogVisible = false;
    this.aiDashboardHtml = null;
    this.aiDashboardSrc = null;
    this._revokeBlobUrl();
    // Pass instructions (may be empty — agent treats empty as no extra instructions)
    this.onGenerate(this.regenInstructions.trim() || undefined);
  }

  /** Downloads the generated HTML as a local file named "<analysis name> AI Dashboard.html". */
  onDownload(): void {
    if (!this.aiDashboardHtml) return;
    this.downloadLoading.set(true);
    try {
      const analysisName = this.geoReportDataService.analysisReport()?.displayName?.trim() || 'Geotechnical Analysis';
      // Sanitise the name so it is safe to use as a filename
      const safeName = analysisName.replace(/[/\\?%*:|"<>]/g, '-');
      const filename = `${safeName} AI Dashboard.html`;

      const blob = new Blob([this.aiDashboardHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.toast.success('AI Dashboard downloaded');
    } catch {
      this.toast.error('Failed to download AI Dashboard');
    } finally {
      this.downloadLoading.set(false);
    }
  }

  // ── History actions ───────────────────────────────────────────────────────

  onHistoryClick(): void {
    if (!this.analysisId) return;
    this.openHistory = true;
    this.loadingHistory = true;
    this.geotechnicalAnalysisService.aiDashboardVersionList(this.analysisId).subscribe({
      next: (res: any) => {
        this.historyList = Array.isArray(res?.data) ? res.data : [];
        this.loadingHistory = false;
      },
      error: () => {
        this.historyList = [];
        this.loadingHistory = false;
      },
    });
  }

  getVersionMenuItems(item: IAiDashboardVersion): CustomMenuItem[] {
    return [
      {
        label: 'View',
        icon: 'pi pi-eye',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => this.onViewVersion(item),
      },
      {
        label: 'Rename',
        icon: 'pi pi-pencil',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => this.onRenameVersion(item),
      },
      {
        label: 'Restore',
        icon: 'pi pi-history',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => this.onRestoreVersion(item),
      },
      {
        label: 'Download',
        icon: 'pi pi-download',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => this.onDownloadVersion(item),
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        iconSource: IconSourceEnum.PRIMENG,
        class: 'text-red-600',
        callback: () => this.onDeleteVersion(item),
      },
    ];
  }

  onViewVersion(item: IAiDashboardVersion): void {
    this.viewVersionTitle = item.name;
    this.viewVersionLoading = true;
    this.viewVersionSrc = null;
    this._revokeViewBlobUrl();
    this.viewDialogVisible = true;

    this.geotechnicalAnalysisService.getAiDashboardVersionSignedUrl(this.analysisId, item._id).subscribe({
      next: async (res: any) => {
        const signedUrl = res?.data?.signedUrl;
        if (!signedUrl) { this.viewVersionLoading = false; return; }
        try {
          const html = await fetch(signedUrl).then(r => r.text());
          const blob = new Blob([html], { type: 'text/html' });
          this._viewBlobUrl = URL.createObjectURL(blob);
          this.viewVersionSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this._viewBlobUrl);
        } catch {
          this.toast.error('Failed to load version preview');
        } finally {
          this.viewVersionLoading = false;
        }
      },
      error: () => {
        this.viewVersionLoading = false;
        this.toast.error('Failed to load version preview');
      },
    });
  }

  onCloseViewDialog(): void {
    this.viewDialogVisible = false;
    this.viewVersionSrc = null;
    this._revokeViewBlobUrl();
  }

  blurTarget(event: Event): void {
    (event.target as HTMLElement)?.blur();
  }

  onRenameCurrentVersion(event: Event): void {
    const target = event.target as HTMLElement;
    const newName = target.textContent?.trim() || '';
    const previousName = this.currentVersionName || '';
    if (!newName || newName === previousName || !this.currentVersionId) {
      target.textContent = previousName;
      return;
    }
    this.geotechnicalAnalysisService.renameAiDashboardVersion(this.analysisId, this.currentVersionId, newName).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.currentVersionName = newName;
          const idx = this.historyList.findIndex(v => v._id === this.currentVersionId);
          if (idx !== -1) this.historyList[idx] = { ...this.historyList[idx], name: newName };
          this.toast.success('Version renamed successfully');
        } else {
          target.textContent = previousName;
        }
      },
      error: () => {
        target.textContent = previousName;
        this.toast.error('Failed to rename version');
      },
    });
  }

  onRenameVersion(item: IAiDashboardVersion): void {
    this._renameTarget = item;
    this.renameInput = item.name;
    this.renameDialogVisible = true;
  }

  onSubmitRename(): void {
    const target = this._renameTarget;
    const name = this.renameInput.trim();
    if (!target || !name) return;
    this.renameDialogVisible = false;
    this.geotechnicalAnalysisService.renameAiDashboardVersion(this.analysisId, target._id, name).subscribe({
      next: (res: any) => {
        if (res?.success) {
          const idx = this.historyList.findIndex(v => v._id === target._id);
          if (idx !== -1) this.historyList[idx] = { ...this.historyList[idx], name };
          if (target._id === this.currentVersionId) this.currentVersionName = name;
          this.toast.success('Version renamed successfully');
        }
      },
      error: () => {
        this.toast.error('Failed to rename version');
      },
    });
  }

  onRestoreVersion(item: IAiDashboardVersion): void {
    if (!this.analysisId) return;
    this.geotechnicalAnalysisService.restoreAiDashboardVersion(this.analysisId, item._id).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.lastUserInstructions = res.data?.userInstructions ?? null;
          this.openHistory = false;
          const currentReport = this.geoReportDataService.analysisReport();
          if (currentReport && item.attachment) {
            this.geoReportDataService.updateData({
              analysisReport: { ...currentReport, aiDashboardAttachment: item.attachment },
            });
          }
          this._loadPersistedDashboard();
          this.toast.success(`"${item.name}" restored`);
        }
      },
      error: () => {
        this.toast.error('Failed to restore version');
      },
    });
  }

  onDownloadVersion(item: IAiDashboardVersion): void {
    if (!this.analysisId) return;
    this.geotechnicalAnalysisService.getAiDashboardVersionSignedUrl(this.analysisId, item._id).subscribe({
      next: async (res: any) => {
        const signedUrl = res?.data?.signedUrl;
        if (!signedUrl) { this.toast.error('Download failed'); return; }
        try {
          const html = await fetch(signedUrl).then(r => r.text());
          const analysisName = this.geoReportDataService.analysisReport()?.displayName?.trim() || 'Geotechnical Analysis';
          const safeName = analysisName.replace(/[/\\?%*:|"<>]/g, '-');
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${safeName} - ${item.name}.html`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          this.toast.success('Downloaded successfully');
        } catch {
          this.toast.error('Failed to download version');
        }
      },
      error: () => {
        this.toast.error('Failed to download version');
      },
    });
  }

  onDeleteVersion(item: IAiDashboardVersion): void {
    if (!this.analysisId) return;
    this.geotechnicalAnalysisService.deleteAiDashboardVersion(this.analysisId, item._id).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.historyList = this.historyList.filter(v => v._id !== item._id);
          this.toast.success('Version deleted');
        }
      },
      error: () => {
        this.toast.error('Failed to delete version');
      },
    });
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Fetches the persisted AI Dashboard status from the backend on init (and after a
   * successful socket event).
   *
   * - Sets `initialLoading` while the call is in flight so the template shows a spinner.
   * - COMPLETED + signedUrl  → downloads the HTML via the signed URL then renders the iframe.
   * - IN_PROGRESS            → shows the lottie animation (generation was running on refresh).
   * - FAILED                 → shows the error / retry state.
   * - NOT_STARTED / unknown  → shows the generate CTA.
   */
  _loadPersistedDashboard(): void {
    if (!this.analysisId) return;

    this.initialLoading = true;

    this.geotechnicalAnalysisService.getAiDashboard(this.analysisId).subscribe({
      next: (res: any) => {
        const data = res?.data;
        this.initialLoading = false;

        if (!data) return;

        // Persist instructions for dialog pre-fill
        this.lastUserInstructions = data.lastUserInstructions ?? null;
        this.workspaceInstructions = data.workspaceInstructions ?? null;

        this.aiDashboardStatus.set(data.status ?? GEOTECHNICAL_AI_DASHBOARD_STATUS.NOT_STARTED);

        if (data.status === GEOTECHNICAL_AI_DASHBOARD_STATUS.COMPLETED && data.signedUrl) {
          // Keep the spinner up while downloading the HTML blob from S3
          this.srcLoading = true;
          fetch(data.signedUrl)
            .then(r => r.text())
            .then(html => {
              this.aiDashboardHtml = html;
              this._buildSrc(html);
            })
            .catch(() => {
              // Non-fatal — fall back to the generate CTA
            })
            .finally(() => {
              this.srcLoading = false;
            });
          this._loadCurrentVersionMeta();
        }
      },
      error: () => {
        this.initialLoading = false;
        // Non-fatal — the NOT_STARTED empty state is already shown by default
      },
    });
  }

  /**
   * Wraps the raw HTML in a Blob URL so the sandboxed iframe can run CDN
   * scripts without violating Angular's content-security policy.
   */
  private _buildSrc(html: string): void {
    this._revokeBlobUrl();
    const blob = new Blob([html], { type: 'text/html' });
    this._blobUrl = URL.createObjectURL(blob);
    this.aiDashboardSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this._blobUrl);
  }

  /** Revokes the current blob URL to free memory. */
  private _revokeBlobUrl(): void {
    if (this._blobUrl) {
      URL.revokeObjectURL(this._blobUrl);
      this._blobUrl = null;
    }
  }

  private _revokeViewBlobUrl(): void {
    if (this._viewBlobUrl) {
      URL.revokeObjectURL(this._viewBlobUrl);
      this._viewBlobUrl = null;
    }
  }

  private _loadCurrentVersionMeta(): void {
    if (!this.analysisId) return;
    this.geotechnicalAnalysisService.aiDashboardVersionList(this.analysisId).subscribe({
      next: (res: any) => {
        const versions: IAiDashboardVersion[] = Array.isArray(res?.data) ? res.data : [];
        const attachmentId = this.geoReportDataService.analysisReport()?.aiDashboardAttachment;
        const current = versions.find(v => v.attachment === attachmentId) ?? versions[0];
        if (current) {
          this.currentVersionId = current._id;
          this.currentVersionName = current.name;
        }
      },
    });
  }

  /** Loads the lottie animation JSON used during the in-progress state. */
  loadGenerationAnimation(): void {
    if (this.generationAnimationData) return;
    fetch('/animations/template_generation.json')
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then(data => { this.generationAnimationData = data; })
      .catch(error => { console.error('Error loading animation:', error); });
  }

  /** Loads the loading-spinner Lottie JSON used for the loading and "In Queue" states. */
  loadQueueAnimation(): void {
    if (this.queueAnimationData) return;
    fetch('/animations/loading-spinner.json')
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then(data => { this.queueAnimationData = data; })
      .catch(error => { console.error('Error loading queue animation:', error); });
  }
}
